import { ICookies } from 'library/ts/utilities/interfaces/ICookies';

/**
 *    :: cookies.js ::
 *
 *    @description A complete cookies reader/writer framework with full unicode support. Refactored as ES6 static class
 *    @see {@link https://github.com/madmurphy/cookies.js}
 *
 *    Revision #5 - August 14th, 2018
 *
 *    https://developer.mozilla.org/en-US/docs/Web/API/document.cookie
 *    https://developer.mozilla.org/User:fusionchess
 *    https://github.com/madmurphy/cookies.js
 *
 *    This framework is released under the GNU Public License, version 3 or later.
 *    http://www.gnu.org/licenses/gpl-3.0-standalone.html
 *
 *    Syntax:
 *
 *    * docCookies.setItem(name, value[, end[, path[, domain[, secure]]]])
 *    * docCookies.getItem(name)
 *    * docCookies.removeItem(name[, path[, domain]])
 *    * docCookies.hasItem(name)
 *    * docCookies.keys()
 *    * docCookies.clear([path[, domain]])
 *
 */
export class Cookies implements ICookies {

	public static _name: string = 'Cookies';
	private static _instance: ICookies = new Cookies();

	constructor() {
		if ( Cookies._instance ) {
			throw new Error( Cookies._name + ' is a Singleton and can only be accessed through ' + Cookies._name + '.getInstance()' );
		}
		Cookies._instance = this;
	}

	public static getInstance(): ICookies {
		return Cookies._instance;
	}

	/**
	 * @description Returns parsed document.cookie
	 * @see {@link https://gist.github.com/rendro/525bbbf85e84fa9042c2|Parse document.cookie to Object}
	 * @return {object}
	 */
	docCookie = (): Record<string, unknown> => {
		// PLBCK-5070: Replace Object.fromEntries with ES5-compatible code for Samsung Tizen support
		const out: Record<string, unknown> = {};
		document.cookie.split( /; */ ).forEach(
			( cookie ) => {
				const [ key, value ] = cookie.split( /=(.*)$/, 2 );
				if ( key !== '' ) {
					out[ key ] = decodeURIComponent( value );
				}
			}
		);
		// return parsed cookie object
		return out;
	};

	/**
	 * @description Get cookie
	 * @param {string} sKey
	 * @returns {any}
	 */
	getItem = ( sKey: string ): any => {
		if ( !sKey ) {
			return null;
		}
		// return decodeURIComponent( document.cookie.replace( new RegExp( "(?:(?:^|.*;)\\s*" + encodeURIComponent( sKey ).replace( /[\-\.\+\*]/g, "\\$&" ) + "\\s*\\=\\s*([^;]*).*$)|^.*$" ), "$1" ) ) || null;
		return decodeURIComponent( document.cookie.replace( new RegExp( '(?:(?:^|.*;)\\s*' + encodeURIComponent( sKey ).replace( /[\-\.\+\*]/g, '\\$&' ) + '\\s*\\=\\s*([^;]*).*$)|^.*$' ), '$1' ) ) || null;
	};

	/**
	 * @description Set item
	 * @param {string} sKey
	 * @param sValue
	 * @param vEnd
	 * @param {string} sPath
	 * @param {string} sDomain
	 * @param bSecure
	 * @returns {boolean}
	 */
	setItem = ( sKey: string, sValue: any, vEnd: any, sPath: string, sDomain: string, bSecure: any ): boolean => {
		if ( !sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test( sKey ) ) {
			return false;
		}
		let sExpires = '';
		if ( vEnd ) {
			switch ( vEnd.constructor ) {
				case Number:
					sExpires = vEnd === Infinity ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT' : '; max-age=' + vEnd;
					/*
					Note: Despite officially defined in RFC 6265, the use of `max-age` is not compatible with any
					version of Internet Explorer, Edge and some mobile browsers. Therefore passing a number to
					the end parameter might not work as expected. A possible solution might be to convert the the
					relative time to an absolute time. For instance, replacing the previous line with:
					*/
					/*
					sExpires = vEnd === Infinity ? "; expires=Fri, 31 Dec 9999 23:59:59 GMT" : "; expires=" + (new Date(vEnd * 1e3 + Date.now())).toUTCString();
					*/
					break;
				case String:
					sExpires = '; expires=' + vEnd;
					break;
				case Date:
					sExpires = '; expires=' + vEnd.toUTCString();
					break;
			}
		}
		document.cookie = encodeURIComponent( sKey ) + '=' + encodeURIComponent( sValue ) + sExpires + ( sDomain ? '; domain=' + sDomain : '' ) + ( sPath ? '; path=' + sPath : '' ) + ( bSecure ? '; secure' : '' );
		return true;
	};

	/**
	 * @description Remove item
	 * @param {string} sKey
	 * @param {string} sPath
	 * @param {string} sDomain
	 * @returns {boolean}
	 */
	removeItem = ( sKey: string, sPath: string, sDomain: string ): boolean => {
		if ( !this.hasItem( sKey ) ) {
			return false;
		}
		document.cookie = encodeURIComponent( sKey ) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' + ( sDomain ? '; domain=' + sDomain : '' ) + ( sPath ? '; path=' + sPath : '' );
		return true;
	};

	/**
	 * @description Has item?
	 * @param {string} sKey
	 * @returns {boolean}
	 */
	hasItem = ( sKey: string ): boolean => {
		if ( !sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test( sKey ) ) {
			return false;
		}
		return ( new RegExp( '(?:^|;\\s*)' + encodeURIComponent( sKey ).replace( /[\-\.\+\*]/g, '\\$&' ) + '\\s*\\=' ) ).test( document.cookie );
	};

	keys = (): any => {
		const aKeys = document.cookie.replace( /((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:|$)/g, '' ).split( /\s*(?:\=[^;]*)?;\s*/ );
		for ( let nLen = aKeys.length, nIdx = 0; nIdx < nLen; nIdx++ ) {
			aKeys[ nIdx ] = decodeURIComponent( aKeys[ nIdx ] );
		}
		return aKeys;
	};

	/**
	 * @description Clears cookie
	 * @param {string} sPath
	 * @param {string} sDomain
	 */
	clear = ( sPath: string, sDomain: string ): void => {
		const aKeys = this.keys();
		for ( let nLen = aKeys.length, nIdx = 0; nIdx < nLen; nIdx++ ) {
			this.removeItem( aKeys[ nIdx ], sPath, sDomain );
		}
	};
}